import { Component } from '@theme/component';
import { observeEdges } from '@theme/ca-scroll-edges';
import { analyticsAllowed, flushClick, stashClick } from '@theme/ca-click-tracking';
import { bindScrollArrows } from '@theme/ca-scroll-arrows';
import { fillBestSellers } from '@theme/ca-subrail-fill';

const STORAGE_KEY = 'ca_subnav_click';
const FADE_FALLBACK_PX = 40;

/**
 * @param {Element | null} scroller
 * @param {Element | null} target
 */
function reveal(scroller, target) {
  if (!(scroller instanceof HTMLElement) || !(target instanceof HTMLElement) || !scroller.clientWidth) return;
  const style = getComputedStyle(scroller);
  if (style.direction === 'rtl') return;
  const fade = fadeWidth(style);
  const start = parseFloat(style.paddingInlineStart) || 0;
  const box = scroller.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  const left = rect.left - box.left - scroller.clientLeft;
  const right = rect.right - box.left - scroller.clientLeft;
  const visibleEnd = scroller.clientWidth - (fitsWithoutTrail(scroller, fade) ? 0 : fade);

  const inset = scroller.scrollLeft > 0 ? Math.max(start, fade) : start;

  if (left < inset) scroller.scrollLeft += left - inset;
  else if (right > visibleEnd) scroller.scrollLeft += right - visibleEnd;
}

/** @param {CSSStyleDeclaration} style */
function fadeWidth(style) {
  return parseFloat(style.getPropertyValue('--ca-subnav-fade')) || FADE_FALLBACK_PX;
}

/**
 * @param {HTMLElement} scroller
 * @param {number} fade
 */
function fitsWithoutTrail(scroller, fade) {
  return scroller.scrollWidth - scroller.clientWidth <= fade + 1;
}

/** @param {string} handle */
function publishPending(handle) {
  flushClick({
    key: STORAGE_KEY,
    eventName: 'ca_subnav_click',
    matchField: 'to_handle',
    matchValue: handle,
    build: (record, text) => ({
      level: text(record.level),
      from_handle: text(record.from_handle),
      to_handle: text(record.to_handle),
      group: text(record.group),
      role: text(record.role),
      position: Number(record.position) || 0,
    }),
  });
}

/** @param {HTMLElement} track */
function railStep(track) {
  const first = track.firstElementChild;
  const pitch = first ? first.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : 0;
  return pitch ? Math.max(1, Math.floor(track.clientWidth / pitch) - 1) * pitch : track.clientWidth * 0.8;
}

class CaSubnav extends Component {
  /** @type {Map<Element, { update: () => void, disconnect: () => void }>} */
  #edges = new Map();

  /** @type {{ disconnect: () => void }[]} */
  #rail = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusIn);
    for (const scroller of this.querySelectorAll('[data-scroller]')) {
      if (!(scroller instanceof HTMLElement)) continue;
      this.#edges.set(
        scroller,
        observeEdges(scroller, undefined, {
          tolerance: 1,
          slack: scroller.hasAttribute('data-rail') ? undefined : () => fadeWidth(getComputedStyle(scroller)) + 1,
        })
      );
    }
    this.#bindRail();
    publishPending(this.dataset.from || '');
    requestAnimationFrame(() => this.#revealCurrent());
    document.fonts?.ready.then(() => this.#revealCurrent());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusIn);
    for (const edges of this.#edges.values()) edges.disconnect();
    this.#edges.clear();
    for (const part of this.#rail) part.disconnect();
    this.#rail = [];
  }

  #bindRail() {
    const track = this.querySelector('[data-rail]');
    if (!(track instanceof HTMLElement)) return;
    const prev = this.querySelector('[data-rail-prev]');
    const next = this.querySelector('[data-rail-next]');
    const buttons = [prev, next].filter((button) => button instanceof HTMLElement);
    const arrows = bindScrollArrows(
      track,
      prev instanceof HTMLElement ? prev : null,
      next instanceof HTMLElement ? next : null,
      {
        step: () => railStep(track),
        onChange: ({ overflowing }) => {
          for (const button of buttons) {
            if (button.hidden === overflowing) button.hidden = !overflowing;
            if (button.getAttribute('aria-disabled') === 'true') {
              if (button.tabIndex !== -1) button.tabIndex = -1;
            } else if (button.hasAttribute('tabindex')) {
              button.removeAttribute('tabindex');
            }
          }
        },
      }
    );
    this.#rail.push(arrows, fillBestSellers(this));
  }

  /**
   * @param {Event} event
   */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement)) return;
    this.#activate(tab);
    reveal(tab.closest('[data-scroller]'), tab);
  }

  /**
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    const tabs = this.#tabs();
    const origin = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const index = origin instanceof HTMLElement ? tabs.indexOf(origin) : -1;
    if (index < 0) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = index + (rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = index + (rtl ? 1 : -1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    if (!tab) return;
    this.#activate(tab);
    tab.focus({ preventScroll: true });
    reveal(tab.closest('[data-scroller]'), tab);
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /**
   * @param {HTMLElement} selected
   */
  #activate(selected) {
    for (const tab of this.#tabs()) {
      const on = tab === selected;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !on;
      if (panel && on) this.#edges.get(panel.querySelector('[data-scroller]') || panel)?.update();
    }
  }

  #revealCurrent() {
    if (!this.isConnected) return;
    for (const scroller of this.querySelectorAll('[data-scroller]')) {
      reveal(scroller, scroller.querySelector('[aria-current="page"], [aria-selected="true"]'));
    }
    for (const edges of this.#edges.values()) edges.update();
  }

  /**
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target instanceof Element ? event.target.closest('a, [role="tab"]') : null;
    if (target) reveal(target.closest('[data-scroller]'), target);
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-role]') : null;
    if (!(link instanceof HTMLElement) || !this.contains(link)) return;

    if (!analyticsAllowed()) return;

    stashClick(STORAGE_KEY, {
      level: this.dataset.level || '',
      from_handle: this.dataset.from || '',
      to_handle: link.dataset.toHandle || '',
      group: link.dataset.group || '',
      role: link.dataset.role || '',
      position: Number(link.dataset.position) || 0,
    });
  };
}

const heroHandle = document.querySelector('[data-ca-collection-handle]')?.getAttribute('data-ca-collection-handle');
if (heroHandle) publishPending(heroHandle);

if (!customElements.get('ca-subnav')) {
  customElements.define('ca-subnav', CaSubnav);
}
